// Port of ios/Sources/ViewModels/TeamBadges.swift.
//
// Per-team attention for the header's team strip — the sum of unmuted mention counts and an
// "anything unread" flag, per team, from the SAME `ChannelListDerivation.unreadState` rule the
// sidebar rows use. Mirrors Android's `TeamBadgesDerivation` decision for decision.
//
// DM/GM conversations carry no team id and follow the user into every team, so they light up
// no team's logo: a badge that appears on every tile says nothing.
import { unreadState } from './channelListDerivation';
import type { ChannelMemberRecord, ChannelRecord } from './records';

export interface Badge {
  mentionCount: number;
  hasUnread: boolean;
}

/** Team id → badge. A team with nothing to say has NO entry (the tile draws nothing). */
export function compute(args: {
  channels: readonly ChannelRecord[];
  membersByChannelId: ReadonlyMap<string, ChannelMemberRecord>;
  /**
   * Channels whose newest ROOT post the reader wrote — see
   * `ChannelListDerivation.unreadState`. Passed so a team tile can never claim unread the
   * sidebar rows underneath deny.
   */
  ownLatestRootPostChannelIds?: ReadonlySet<string>;
  /**
   * Channels in muted categories (`CategoriesDAO.mutedChannelIds`) — a muted category
   * must not light a team tile any more than a muted channel does.
   */
  mutedChannelIds?: ReadonlySet<string>;
}): Map<string, Badge> {
  const own = args.ownLatestRootPostChannelIds ?? new Set<string>();
  const muted = args.mutedChannelIds ?? new Set<string>();
  const result = new Map<string, Badge>();
  for (const channel of args.channels) {
    if (channel.teamId === '' || channel.deleteAt !== 0) continue;
    const state = unreadState(channel, args.membersByChannelId.get(channel.id), {
      latestPostIsOwnRoot: own.has(channel.id),
      categoryMuted: muted.has(channel.id),
    });
    if (state.isMuted) continue;
    if (!(state.isUnread || state.mentionCount > 0)) continue;
    const previous = result.get(channel.teamId) ?? { mentionCount: 0, hasUnread: false };
    result.set(channel.teamId, {
      mentionCount: previous.mentionCount + state.mentionCount,
      hasUnread: previous.hasUnread || state.isUnread,
    });
  }
  return result;
}

/** Namespace form, for `TeamBadges.compute(...)` call sites. */
export const TeamBadges = { compute } as const;
